import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta, shortDate } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, Plus, Pencil, Trash2, FileSignature, Users, ShieldCheck } from "lucide-react";

function TemplateDialog({ t, initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [title, setTitle] = useState(initial.title || "");
  const [body, setBody] = useState(initial.body || "");
  const [required, setRequired] = useState(initial.required_before_booking !== false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (title.trim().length < 1) { toast.error(t("cf.err_title")); return; }
    if (body.trim().length < 1) { toast.error(t("cf.err_body")); return; }
    setSaving(true);
    const payload = { title: title.trim(), body: body.trim(), required_before_booking: required, version: initial.version || 1 };
    try {
      if (isEdit) await api.patch(`/consent-forms/templates/${initial.id}`, payload);
      else await api.post("/consent-forms/templates", payload);
      toast.success(t("cf.saved"));
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="cf-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? t("cf.dialog_edit") : t("cf.dialog_new")}</DialogTitle>
          <DialogDescription>{t("cf.dialog_desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("cf.f_title")}</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("cf.f_title_ph")} data-testid="cf-title" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("cf.f_body")}</label>
            <Textarea className="min-h-[200px]" value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("cf.f_body_ph")} data-testid="cf-body" />
          </div>
          <div className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
            <div className="flex min-w-0 items-center gap-2">
              <ShieldCheck className={`h-4 w-4 shrink-0 ${required ? "text-primary" : "text-muted-foreground"}`} />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{t("cf.f_required")}</p>
                <p className="text-xs text-muted-foreground">{t("cf.f_required_hint")}</p>
              </div>
            </div>
            <Switch checked={required} onCheckedChange={setRequired} data-testid="cf-required" />
          </div>
          {isEdit && <p className="text-xs italic text-muted-foreground">{t("cf.version_note")}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose} data-testid="cf-cancel">{t("common.cancel")}</Button>
          <Button className="rounded-full" onClick={save} disabled={saving} data-testid="cf-save">{saving ? t("common.saving") : t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignaturesDialog({ t, template, onClose }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/consent-forms/templates/${template.id}/signatures`)
      .then(({ data }) => setRows(data?.signatures || [])).catch(() => setRows([]));
  }, [template.id]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="cf-signatures-dialog">
        <DialogHeader>
          <DialogTitle>{t("cf.sig_title")}</DialogTitle>
          <DialogDescription>{template.title}</DialogDescription>
        </DialogHeader>
        {rows === null ? (
          <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
        ) : rows.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("cf.sig_empty")}</p>
        ) : (
          <div className="space-y-2">
            {rows.map((s) => (
              <div key={s.id} className="rounded-xl border border-border bg-card p-3" data-testid={`cf-signature-${s.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <p className="min-w-0 flex-1 truncate font-medium text-foreground">{s.signer_name || s.client_name}</p>
                  <span className="shrink-0 text-xs text-muted-foreground">{s.signed_at ? shortDate(s.signed_at) : ""}</span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">{t("cf.sig_version", { v: s.template_version })}</p>
                {s.signature_png_b64 && (
                  <img src={s.signature_png_b64.startsWith("data:") ? s.signature_png_b64 : `data:image/png;base64,${s.signature_png_b64}`} alt="" className="mt-2 h-20 rounded-lg border border-border bg-white object-contain" />
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ConsentForms() {
  const { user } = useAuth();
  const t = useT();
  usePageMeta(t("cf.meta_title"));
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [sigs, setSigs] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/consent-forms/templates"); setRows(data?.templates || []); }
    catch { setRows([]); }
  }, []);
  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const remove = async (tpl) => {
    if (!window.confirm(t("cf.delete_confirm"))) return;
    try { await api.delete(`/consent-forms/templates/${tpl.id}`); toast.success(t("cf.deleted")); await load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="coach-consent-forms-page">
      <Seo title={t("cf.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="cf-back">
        <ChevronLeft className="h-4 w-4" /> {t("cf.back")}
      </Link>
      <PageHeader eyebrow={t("cf.eyebrow")} title={t("cf.title")} subtitle={t("cf.subtitle")}
        action={<Button className="gap-1.5 rounded-full" onClick={() => setEditing({})} data-testid="cf-new"><Plus className="h-4 w-4" /> {t("cf.new")}</Button>} />

      {rows === null ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="cf-empty">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><FileSignature className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("cf.empty_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("cf.empty_sub")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((tpl) => (
            <div key={tpl.id} data-testid={`cf-template-${tpl.id}`} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><FileSignature className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-semibold text-foreground">{tpl.title}</p>
                    <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">v{tpl.version}</span>
                    {tpl.required_before_booking && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">{t("cf.badge_required")}</span>}
                  </div>
                  <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{tpl.body}</p>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <button onClick={() => setSigs(tpl)} data-testid={`cf-view-sigs-${tpl.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent">
                  <Users className="h-3.5 w-3.5" /> {t("cf.sig_count", { n: tpl.signatures_count ?? 0 })}
                </button>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full" onClick={() => setEditing(tpl)} data-testid={`cf-edit-${tpl.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-9 w-9 rounded-full text-rose-600" onClick={() => remove(tpl)} data-testid={`cf-del-${tpl.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <TemplateDialog t={t} initial={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />}
      {sigs && <SignaturesDialog t={t} template={sigs} onClose={() => setSigs(null)} />}
    </div>
  );
}
